import gsap from 'gsap';
import { LS_COUNTRY_KEY, LS_LANGUAGE_KEY, persistMarketPreference } from '../utils/market-preference';
import appStore from '../utils/state';
import { MarketModalType } from '../types';

type MarketLanguage = {
  iso_code: string;
  name: string;
};

type MarketCountryEntry = {
  currency: {
    iso_code: string;
    symbol: string;
  };
  market: string;
  languages: MarketLanguage[];
};

type MarketsMap = Record<string, MarketCountryEntry>;

type ShopifyCustomerPrivacy = {
  getRegion: () => string;
};

type ShopifyGlobal = {
  customerPrivacy?: ShopifyCustomerPrivacy;
  loadFeatures?: (
    features: Array<{ name: string; version: string }>,
    callback: (error: Error | null | undefined) => void,
  ) => void;
};

function getShopify(): ShopifyGlobal | undefined {
  return (window as Window & { Shopify?: ShopifyGlobal }).Shopify;
}

export default class MarketModal extends HTMLElement {
  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  private dialog: HTMLElement | null = null;
  private content: HTMLElement | null = null;
  private form: HTMLFormElement | null = null;
  private countrySelect: HTMLSelectElement | null = null;
  private languageSelect: HTMLSelectElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private dataScript: HTMLScriptElement | null = null;
  private titleElement: HTMLElement | null = null;

  private marketsMap: MarketsMap = {};
  private currentCountry = '';
  private currentLanguage = '';
  private detectedCountry: string | null = null;
  private hasDecided = false;

  private triggerElement: HTMLElement | null = null;

  private unsubscribeFromMarketModalOpenState: (() => void) | null = null;
  private unsubscribeFromMarketModalTypeState: (() => void) | null = null;
  private onVisitorConsentCollectedBound = this.onVisitorConsentCollected.bind(this);

  private onMarketModalToggleBound = this.onMarketModalToggle.bind(this);
  private onMarketModalTypeUpdateBound = this.onMarketModalTypeUpdate.bind(this);
  private onCloseButtonClickBound = this.onCloseButtonClick.bind(this);
  private onCountryChangeBound = this.onCountryChange.bind(this);
  private onFormSubmitBound = this.onFormSubmit.bind(this);
  private onDialogClickBound = this.onDialogClick.bind(this);

  connectedCallback(): void {
    this.dialog = this.querySelector('#market-modal-dialog');
    this.content = this.querySelector('[data-market-modal-ref="content"]');
    this.form = this.querySelector('form');
    this.countrySelect = this.querySelector('[data-market-modal-ref="country-select"]');
    this.languageSelect = this.querySelector('[data-market-modal-ref="language-select"]');
    this.closeButton = this.querySelector('[data-market-modal-ref="close-button"]');
    this.dataScript = this.querySelector('[data-market-modal-ref="data-script"]');
    this.titleElement = this.querySelector('[data-market-modal-ref="title"]');

    this.currentCountry = (this.dataset.currentCountry ?? '').toUpperCase();
    this.currentLanguage = (this.dataset.currentLanguage ?? '').toLowerCase();
    this.marketsMap = this.parseMarketsMap();

    this.addEventListeners();
    this.subscribe();
    this.bootstrapCustomerPrivacy();
  }

  private parseMarketsMap(): MarketsMap {
    const script = this.dataScript;
    if (!script?.textContent) {
      return {};
    }

    try {
      return JSON.parse(script.textContent) as MarketsMap;
    } catch {
      return {};
    }
  }

  private bootstrapCustomerPrivacy(): void {
    const shopify = getShopify();

    if (shopify?.customerPrivacy?.getRegion) {
      this.decideVisibility();
      return;
    }

    document.addEventListener('visitorConsentCollected', this.onVisitorConsentCollectedBound);

    if (typeof shopify?.loadFeatures === 'function') {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) {
          this.decideVisibility();
          return;
        }
        this.decideVisibility();
      });
      return;
    }

    window.setTimeout(() => {
      this.decideVisibility();
    }, 1000);
  }

  private onVisitorConsentCollected(): void {
    this.decideVisibility();
  }

  private getDetectedCountryCode(): string | null {
    const shopify = getShopify();
    const region = shopify?.customerPrivacy?.getRegion?.() ?? '';
    if (!region) {
      return null;
    }
    return region.slice(0, 2).toUpperCase() || null;
  }

  private getNavigatorLanguageCode(): string {
    return (navigator.language || 'en').split('-')[0]?.toLowerCase() || 'en';
  }

  private getCountryName(code: string, locale = 'en'): string | null {
    try {
      const regionNames = new Intl.DisplayNames([locale], { type: 'region' });
      return regionNames.of(code.toUpperCase()) || null;
    } catch {
      return null;
    }
  }

  private applyTitle(mode: 'geo' | 'redirect' | 'update', countryCode: string): void {
    if (!countryCode) {
      return;
    }

    const countryName = this.getCountryName(countryCode, this.currentLanguage || 'en');
    if (!countryName) {
      return;
    }

    let template = '';

    switch (mode) {
      case 'geo':
        template = this.dataset.geoTitle ?? '';
        break;
      case 'redirect':
        template = this.dataset.redirectTitle ?? '';
        break;
      case 'update':
        template = this.dataset.updateTitle ?? '';
        break;
    }

    if (this.titleElement) {
      this.titleElement.textContent = template.replace('([country])', countryName);
    }
  }

  private resolvePreferredLanguage(countryCode: string, preferredLanguage?: string | null): string {
    const entry = this.marketsMap[countryCode];
    if (!entry?.languages?.length) {
      return preferredLanguage || this.currentLanguage || this.getNavigatorLanguageCode();
    }

    if (preferredLanguage && entry.languages.some((language) => language.iso_code === preferredLanguage)) {
      return preferredLanguage;
    }

    return entry.languages[0].iso_code;
  }

  private isCurrentSetup(country?: string | null, language?: string | null): boolean {
    return (
      (country ?? '').toUpperCase() === this.currentCountry && (language ?? '').toLowerCase() === this.currentLanguage
    );
  }

  private decideVisibility(): void {
    if (this.hasDecided) {
      return;
    }
    this.hasDecided = true;

    document.removeEventListener('visitorConsentCollected', this.onVisitorConsentCollectedBound);

    const storedCountry = localStorage.getItem(LS_COUNTRY_KEY)?.toUpperCase() || null;
    const storedLanguage = localStorage.getItem(LS_LANGUAGE_KEY)?.toLowerCase() || null;

    this.detectedCountry = this.getDetectedCountryCode();
    const availableDetectedCountry =
      this.detectedCountry && this.marketsMap[this.detectedCountry] ? this.detectedCountry : null;

    if (!storedCountry || !storedLanguage) {
      const countryForPreselect =
        availableDetectedCountry || this.currentCountry || Object.keys(this.marketsMap)[0] || '';
      const languageForPreselect = this.resolvePreferredLanguage(countryForPreselect, this.getNavigatorLanguageCode());

      this.preselect(countryForPreselect, languageForPreselect);
      appStore.actions.openMarketModal();
      appStore.actions.updateMarketModalType(this.detectedCountry ? 'geo' : 'redirect');
      return;
    }

    if (this.isCurrentSetup(storedCountry, storedLanguage)) {
      return;
    }

    this.preselect(storedCountry, storedLanguage);
    appStore.actions.openMarketModal();
    appStore.actions.updateMarketModalType('redirect');
  }

  private preselect(countryCode: string, languageCode: string): void {
    if (!this.countrySelect) {
      return;
    }

    const resolvedCountry = this.marketsMap[countryCode]
      ? countryCode
      : this.currentCountry && this.marketsMap[this.currentCountry]
        ? this.currentCountry
        : Object.keys(this.marketsMap)[0] || '';

    if (!resolvedCountry) {
      return;
    }

    this.countrySelect.value = resolvedCountry;
    this.syncFromCountry(resolvedCountry, languageCode);
  }

  addEventListeners(): void {
    this.closeButton?.addEventListener('click', this.onCloseButtonClickBound);
    this.countrySelect?.addEventListener('change', this.onCountryChangeBound);
    this.form?.addEventListener('submit', this.onFormSubmitBound);

    this.dialog?.addEventListener('click', this.onDialogClickBound);
  }

  removeEventListeners(): void {
    this.closeButton?.removeEventListener('click', this.onCloseButtonClickBound);
    this.countrySelect?.removeEventListener('change', this.onCountryChangeBound);
    this.form?.removeEventListener('submit', this.onFormSubmitBound);
    document.removeEventListener('visitorConsentCollected', this.onVisitorConsentCollectedBound);

    this.dialog?.removeEventListener('click', this.onDialogClickBound);
  }

  subscribe(): void {
    this.unsubscribeFromMarketModalOpenState = appStore.subscribeKey(
      'isMarketModalOpen',
      this.onMarketModalToggleBound,
    );
    this.unsubscribeFromMarketModalTypeState = appStore.subscribeKey(
      'marketModalType',
      this.onMarketModalTypeUpdateBound,
    );
  }

  unsubscribe(): void {
    this.unsubscribeFromMarketModalOpenState?.();
    this.unsubscribeFromMarketModalTypeState?.();
  }

  private getFormSelection(): { country: string | undefined; language: string | undefined } {
    return {
      country: this.countrySelect?.value,
      language: this.languageSelect?.value,
    };
  }

  private onFormSubmit(event: SubmitEvent): void {
    const { country, language } = this.getFormSelection();

    persistMarketPreference(country, language);

    if (!this.isCurrentSetup(country, language)) {
      return;
    }

    event.preventDefault();
    appStore.actions.closeMarketModal();
  }

  private onCountryChange(): void {
    const preferredLanguage = this.languageSelect?.value || this.getNavigatorLanguageCode();
    this.syncFromCountry(this.countrySelect?.value ?? '', preferredLanguage);
  }

  private syncFromCountry(countryCode: string, preferredLanguage?: string | null): void {
    const entry = this.marketsMap[countryCode];
    if (!entry || !this.languageSelect) {
      return;
    }

    this.languageSelect.replaceChildren();

    for (const language of entry.languages) {
      const option = document.createElement('option');
      option.value = language.iso_code;
      option.lang = language.iso_code;
      option.textContent = language.name;
      this.languageSelect.appendChild(option);
    }

    this.languageSelect.value = this.resolvePreferredLanguage(countryCode, preferredLanguage);
  }

  private dismissWithCurrentSetup(): void {
    appStore.actions.closeMarketModal();
  }

  onCloseButtonClick(): void {
    this.dismissWithCurrentSetup();
  }

  onDialogClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      this.dismissWithCurrentSetup();
    }
  }

  captureTrigger(): void {
    const trigger = document.querySelector('[aria-controls="market-modal-dialog"]');

    if (trigger instanceof HTMLElement) {
      this.triggerElement = trigger;
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      this.triggerElement = activeElement;
    }
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  onMarketModalToggle(isMarketModalOpen: boolean): void {
    if (isMarketModalOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  onMarketModalTypeUpdate(type: MarketModalType): void {
    if (type === null) {
      return;
    }

    const countryForTitle =
      type === 'geo' ? this.detectedCountry || this.countrySelect?.value || this.currentCountry : this.currentCountry;
    this.applyTitle(type, countryForTitle);
  }

  show(): void {
    if (!this.dialog) {
      return;
    }

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    gsap.set(this.dialog, { autoAlpha: 0, visibility: 'visible' });

    this.captureTrigger();

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this.dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this.content, { y: 32 }, { y: 0, duration: 0.7, ease: 'power3.out' }, 0);
  }

  hide(): void {
    if (!this.dialog) {
      return;
    }

    persistMarketPreference(this.currentCountry, this.currentLanguage);

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline({
      onComplete: () => {
        this.restoreFocus();
      },
    });

    this.hideTl.to(this.dialog, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }
  }
}

customElements.define('market-modal', MarketModal);
